// WHO THIS CLIENT IS — the standing profile, edited where it is read.
//
// IT IS NOT A NEW FIELD. This is `clients.notes`, the column migration 002 created and
// migration 057's header named "general info about them" — one of three questions about
// a client that each got a different home, and the only one shaped like prose:
//
//   when to get back to them   clients.follow_up_on   a DATE      (the lead form)
//   WHO THEY ARE               clients.notes          THIS        one field, overwritten
//   what was said, and when    client_notes           a TABLE     (the contact log)
//
// A second column for "the profile" would have been that middle row twice over, with no
// rule to say which of the two a sentence belongs in — and it would have split by SCREEN
// rather than by question: everything learned about a prospect would stay behind in the
// lead form's field on the day they were won, because advanceStage copies nothing
// between columns. One question, one column, two places to type it.
//
// What was actually missing was a way to EDIT it here. The card showed the text
// read-only under a borrowed label (he.jobs.description, "תיאור"), and ClientModal
// carried the value through its payload without rendering a field for it — so on a won
// customer this text was visible and unreachable, typable only from a screen that client
// had already left.
//
// INLINE, NOT A MODAL. A profile is prose that grows over years; it is read far more
// often than written, and both happen in the same place. A dialog would put the thing
// you are extending behind a click, under the payment terms.
//
// NO MONEY READS IT, and nothing parses it. `pre-wrap` so the paragraph breaks someone
// typed survive — the shape of what they wrote is part of what they wrote.
import { useState } from 'react';
import { clientsApi } from '../../api';
import { messageForError } from '../../i18n/errors';
import he from '../../i18n/he';
import Button from '../../components/Button';
import { Textarea } from '../../components/Form';

export default function ProfileCard({ client, onSaved }) {
  const [editing, setEditing] = useState(false);

  return (
    <div className="card client-section">
      <div className="client-section-head">
        <h2>{he.clientCard.profile.heading}</h2>
        {!editing && (
          <Button small variant="ghost" onClick={() => setEditing(true)}>{he.common.edit}</Button>
        )}
      </div>
      {editing ? (
        <ProfileForm
          client={client}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); onSaved(); }}
        />
      ) : (
        <p className={client.notes ? 'client-profile-body' : 'muted'}>
          {client.notes || he.clientCard.profile.empty}
        </p>
      )}
    </div>
  );
}

function ProfileForm({ client, onCancel, onSaved }) {
  const [text, setText] = useState(client.notes || '');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // `name` rides along because the server's update merges every OMITTED column with the
  // stored row but takes the name straight from the body — it is the one required field,
  // and a payload without it is a 400, not a no-op. Everything else this form does not
  // send keeps its value (clients/service.js `keep`).
  //
  // Emptied to '' rather than null so clearing the box is a save, not a rejected field:
  // a profile someone deliberately blanked is a legitimate state.
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await clientsApi.update(client.id, { name: client.name, notes: text.trim() });
      onSaved();
    } catch (e) { setError(messageForError(e)); setSaving(false); }
  };

  return (
    <>
      <Textarea
        label={he.clientCard.profile.label}
        value={text}
        rows={6}
        onChange={(e) => setText(e.target.value)}
        placeholder={he.clientCard.profile.placeholder}
      />
      <div className="row">
        <Button small onClick={save} disabled={saving}>{he.common.save}</Button>
        <Button small variant="subtle" onClick={onCancel}>{he.common.cancel}</Button>
      </div>
      {error && <p className="error-text small-note">{error}</p>}
    </>
  );
}
